import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useFormatters, useI18n } from '../../i18n';
import { useTheme } from '../../lib/theme';
import type { MapView, ViewMode } from '../../lib/renderer';
import { indexById, scoreKeys, toMapDocument, weightKeys } from '../../lib/vosjson';
import type { VosJsonDocument } from '../../lib/vosjson';
import type { BuildSummary } from '../../lib/types';
import { Callout, CalloutList } from '../../components/Callout';
import { Segmented } from '../../components/Field';
import { MapCanvas } from './MapCanvas';
import { ClusterLegend } from './ClusterLegend';
import { ItemDetailPanel } from './ItemDetailPanel';
import { ExportBar, type ExportTarget } from './ExportBar';
import {
  initialLinkDensity,
  labelsById,
  linesFor,
  neighboursOf,
  searchItems,
  summariseClusters,
} from './mapData';

/**
 * The result screen. Used for the user's own map and for a shared one; those
 * differ only in the banner, the share control and where the exports come from.
 *
 * The map gets the whole viewport minus one toolbar and one 20rem column, and
 * the column scrolls on its own so the canvas never does.
 */
export function MapWorkspace({
  name,
  vos,
  build,
  warnings,
  nDocuments,
  exportTarget,
  banner,
  share,
  actions,
}: {
  name: string;
  /** VOSviewer JSON, exactly as the API returned it. */
  vos: VosJsonDocument;
  build: BuildSummary;
  warnings: readonly string[];
  nDocuments: number;
  exportTarget: ExportTarget;
  /** Rendered above the toolbar; the public route puts its badge here. */
  banner?: ReactNode;
  /** Share control, owned maps only. */
  share?: ReactNode;
  /** Extra toolbar buttons on the right. */
  actions?: ReactNode;
}) {
  const { t } = useI18n();
  const fmt = useFormatters();
  const { resolved } = useTheme();

  const weights = useMemo(() => weightKeys(vos), [vos]);
  const scores = useMemo(() => scoreKeys(vos), [vos]);

  const [viewMode, setViewMode] = useState<ViewMode>('network');
  const [selected, setSelected] = useState<string | null>(null);
  const [weightKey, setWeightKey] = useState<string | null>(null);
  const [scoreKey, setScoreKey] = useState<string | null>(null);
  const [term, setTerm] = useState('');
  const [view, setView] = useState<MapView | null>(null);

  const totalLinks = vos.network.links.length;
  const [density, setDensity] = useState(() => initialLinkDensity(totalLinks));

  // A different map means a different sensible starting density and no stale
  // selection; the toolbar choices are reset with it.
  useEffect(() => {
    setDensity(initialLinkDensity(vos.network.links.length));
    setSelected(null);
    setWeightKey(null);
    setScoreKey(null);
  }, [vos]);

  const activeWeight = weightKey ?? weights[0] ?? null;
  const activeScore = scoreKey ?? scores[0] ?? null;

  // Rebuilding the renderer document is the expensive part, and the weight
  // attribute is the only control that changes it.
  const doc = useMemo(() => toMapDocument(vos, activeWeight), [vos, activeWeight]);

  const items = useMemo(() => indexById(vos), [vos]);
  const labels = useMemo(() => labelsById(vos), [vos]);
  const clusters = useMemo(() => summariseClusters(vos, activeWeight), [vos, activeWeight]);
  const selectedItem = selected ? (items.get(selected) ?? null) : null;
  const neighbours = useMemo(
    () => (selected ? neighboursOf(vos, selected, labels) : []),
    [vos, selected, labels],
  );
  const matches = useMemo(() => searchItems(vos, term), [vos, term]);

  const shown = Math.min(linesFor(totalLinks, density), totalLinks);

  const viewOptions: readonly { value: ViewMode; label: string }[] = [
    { value: 'network', label: t('result.view.network') },
    { value: 'overlay', label: t('result.view.overlay') },
    { value: 'item-density', label: t('result.view.density') },
    { value: 'cluster-density', label: t('result.view.clusterDensity') },
  ];

  return (
    <div className="result">
      {banner}

      <div className="result__bar">
        <span className="result__name" title={name}>
          {name}
        </span>
        <span className="result__stats">
          <span>{t('result.meta.items', { n: fmt.int(build.n_items) })}</span>
          <span>{t('result.meta.links', { n: fmt.int(build.n_links) })}</span>
          <span>{t('result.meta.clusters', { n: fmt.int(build.n_clusters) })}</span>
          {nDocuments > 0 ? <span>{t('result.meta.docs', { n: fmt.int(nDocuments) })}</span> : null}
        </span>

        <span className="header__spacer" />

        <Segmented
          label={t('result.view.label')}
          value={viewMode}
          options={viewOptions}
          onChange={setViewMode}
        />

        {viewMode === 'overlay' && scores.length > 1 ? (
          <>
            <label className="visually-hidden" htmlFor="overlay-select">
              {t('result.overlay.label')}
            </label>
            <select
              id="overlay-select"
              className="select"
              style={{ width: 'auto' }}
              value={activeScore ?? ''}
              onChange={(event) => setScoreKey(event.target.value)}
            >
              {scores.map((key) => (
                <option key={key} value={key}>
                  {key}
                </option>
              ))}
            </select>
          </>
        ) : null}

        {weights.length > 1 ? (
          <>
            <label className="visually-hidden" htmlFor="weight-select">
              {t('result.weight.label')}
            </label>
            <select
              id="weight-select"
              className="select"
              style={{ width: 'auto' }}
              value={activeWeight ?? ''}
              onChange={(event) => setWeightKey(event.target.value)}
            >
              {weights.map((key) => (
                <option key={key} value={key}>
                  {key}
                </option>
              ))}
            </select>
          </>
        ) : null}

        {actions}
      </div>

      <div className="result__body">
        <div className="result__canvas">
          <MapCanvas
            document={doc}
            mode={viewMode}
            scoreField={viewMode === 'overlay' ? activeScore : null}
            maxLines={shown}
            selectedId={selected}
            onSelect={setSelected}
            theme={resolved}
            onReady={setView}
          />
        </div>

        <aside className="result__aside" aria-label={t('result.detail.title')}>
          <div className="aside__section">
            <label className="visually-hidden" htmlFor="find-item">
              {t('result.search.placeholder')}
            </label>
            <input
              id="find-item"
              className="input"
              type="search"
              placeholder={t('result.search.placeholder')}
              value={term}
              onChange={(event) => setTerm(event.target.value)}
            />
            {term.trim() ? (
              matches.length === 0 ? (
                <p className="aside__empty" style={{ marginTop: 'var(--sp-2)' }}>
                  {t('result.search.noMatch')}
                </p>
              ) : (
                <div className="neighbours">
                  {matches.map((hit) => (
                    <button
                      key={hit.id}
                      type="button"
                      className="neighbour"
                      onClick={() => setSelected(hit.id)}
                    >
                      <span>{hit.label}</span>
                    </button>
                  ))}
                </div>
              )
            ) : null}
          </div>

          <div className="aside__section">
            <p className="aside__title">{t('result.linkDensity.label')}</p>
            <input
              className="slider"
              type="range"
              min={0}
              max={100}
              step={1}
              value={Math.round(density * 100)}
              aria-label={t('result.linkDensity.label')}
              onChange={(event) => setDensity(Number(event.target.value) / 100)}
            />
            <p className="field__hint nums">
              {t('result.linkDensity.value', {
                percent: Math.round(density * 100),
                shown: fmt.int(shown),
                total: fmt.int(totalLinks),
              })}
            </p>
            <p className="field__hint">{t('result.linkDensity.hint')}</p>
          </div>

          <div className="aside__section">
            <p className="aside__title">{t('result.detail.title')}</p>
            <ItemDetailPanel item={selectedItem} neighbours={neighbours} onSelect={setSelected} />
          </div>

          <div className="aside__section">
            <p className="aside__title">{t('result.legend.title')}</p>
            <ClusterLegend clusters={clusters} />
          </div>

          {warnings.length > 0 || build.notes.length > 0 ? (
            <div className="aside__section">
              <Callout tone="warn" title={t('result.warnings.title')}>
                <CalloutList items={[...warnings, ...build.notes]} />
              </Callout>
            </div>
          ) : null}

          <div className="aside__section">
            <p className="aside__title">{t('result.build.title')}</p>
            <div className="kv" style={{ marginTop: 0 }}>
              <span className="kv__key">{t('result.build.counted')}</span>
              <span className="kv__value">
                {build.n_items_counted === null ? '—' : fmt.int(build.n_items_counted)}
              </span>
              <span className="kv__key">{t('result.build.dropped')}</span>
              <span className="kv__value">{fmt.int(build.n_dropped_by_thresholds)}</span>
              <span className="kv__key">{t('result.build.quality')}</span>
              <span className="kv__value">
                {build.clustering_quality === null ? '—' : fmt.num(build.clustering_quality)}
              </span>
            </div>
          </div>

          {share ? <div className="aside__section">{share}</div> : null}

          <div className="aside__section">
            <p className="aside__title">{t('result.export.label')}</p>
            <ExportBar target={exportTarget} vos={vos} view={view} name={name} />
          </div>
        </aside>
      </div>
    </div>
  );
}
